import React, { useEffect, useState } from 'react';
import { Table, Switch, Card, Form, Button, Select, Spin, Typography, Alert, Divider, Tag, Space } from 'antd';
import { BellOutlined, UserOutlined, SaveOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import TelegramLinkModal from '../components/TelegramLinkModal';
import ConfirmationNoticeRecipientsCard from '../modules/notifications/components/ConfirmationNoticeRecipientsCard';
import {
  canManageGlobalNotificationSettings,
  canManageNotificationRecipientPool,
} from '../modules/notifications/notificationSettingsAccessPolicy';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';

const { Title, Text } = Typography;

interface NotificationSetting {
  id: string;
  eventType: string;
  enabled: boolean;
  description: string;
}

interface ServiceStatus {
  email: { configured: boolean };
  telegram: { configured: boolean; botUsername: string | null };
}

interface RecipientUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isNotificationRecipient: boolean;
}

interface UserPreferences {
  id?: string;
  userId: string;
  emailEnabled: boolean;
  telegramEnabled: boolean;
  telegramChatId: string | null;
  telegramUsername: string | null;
  language: string;
  isNotificationRecipient?: boolean;
}

const NotificationSettings: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const currentUser = authService.getCurrentUser();
  const canManageGlobalSettings = canManageGlobalNotificationSettings(currentUser);
  const canManageRecipientPool = canManageNotificationRecipientPool(currentUser);
  const canManageAdminSections = canManageGlobalSettings && canManageRecipientPool;
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [recipients, setRecipients] = useState<RecipientUser[]>([]);
  const [updatingRecipientIds, setUpdatingRecipientIds] = useState<Set<string>>(new Set());
  // User preferences state
  const [prefsForm] = Form.useForm();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [telegramModalVisible, setTelegramModalVisible] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const promises: Promise<any>[] = [
        api.get<ServiceStatus>('/notifications/status'),
        api.get<UserPreferences>('/notifications/preferences'),
      ];
      if (canManageAdminSections) {
        promises.push(
          api.get<NotificationSetting[]>('/notifications/settings'),
          api.get<RecipientUser[]>('/notifications/recipients-config')
        );
      }
      const results = await Promise.all(promises);
      setServiceStatus(results[0].data);
      const prefs = results[1].data;
      setPreferences(prefs);
      prefsForm.setFieldsValue({
        emailEnabled: prefs.emailEnabled,
        telegramEnabled: prefs.telegramEnabled,
        language: prefs.language || 'pl',
      });
      if (canManageAdminSections) {
        setSettings(results[2].data);
        setRecipients(results[3].data);
      }
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    const eventTypeLabel = t(`notifications:eventTypes.${settings.find(s => s.id === id)?.eventType}`);
    setUpdatingIds(prev => new Set(prev).add(id));
    try {
      await api.put(`/notifications/settings/${id}`, { enabled });
      setSettings(prev =>
        prev.map(s => (s.id === id ? { ...s, enabled } : s))
      );
      feedback.success(t('notifications:settings_page.saved', { eventType: eventTypeLabel }));
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'notifications:errors.toggleSettingFailed', undefined, { eventType: eventTypeLabel })
      );
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleRecipientToggle = async (userId: string, isNotificationRecipient: boolean) => {
    const recipientName = recipients.find(r => r.id === userId)?.name;
    setUpdatingRecipientIds(prev => new Set(prev).add(userId));
    try {
      await api.put(`/notifications/recipients-config/${userId}`, { isNotificationRecipient });
      setRecipients(prev =>
        prev.map(r => (r.id === userId ? { ...r, isNotificationRecipient } : r))
      );
      feedback.success(t('notifications:recipients.saved', { name: recipientName }));
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'notifications:recipients.saveFailed', undefined, { name: recipientName })
      );
    } finally {
      setUpdatingRecipientIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleSavePrefs = async (values: { emailEnabled: boolean; telegramEnabled: boolean; language: string }) => {
    setSavingPrefs(true);
    try {
      await api.put('/notifications/preferences', values);
      feedback.success(t('notifications:preferences_page.saved'));
      fetchData();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:errors.savePrefsFailed'));
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await api.delete('/notifications/telegram/unlink');
      feedback.success(t('notifications:telegram.unlinkSuccess'));
      fetchData();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:telegram.unlinkFailed'));
    }
  };

  const columns = [
    {
      title: t('notifications:settings_page.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType: string) => (
        <Text strong>{t(`notifications:eventTypes.${eventType}`)}</Text>
      ),
    },
    {
      title: t('notifications:settings_page.description_col'),
      dataIndex: 'eventType',
      key: 'description',
      render: (eventType: string) => t(`notifications:eventDescriptions.${eventType}`),
    },
    {
      title: t('notifications:settings_page.enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean, record: NotificationSetting) => (
        <Switch
          checked={enabled}
          loading={updatingIds.has(record.id)}
          onChange={(checked) => handleToggle(record.id, checked)}
        />
      ),
    },
  ];

  const recipientColumns = [
    {
      title: t('notifications:recipients.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: t('notifications:recipients.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('notifications:recipients.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'ADMIN' ? 'red' : role === 'OPERATIONS' ? 'blue' : 'default'}>
          {t(`common:userRole.${role}`, { defaultValue: role })}
        </Tag>
      ),
    },
    {
      title: t('notifications:recipients.available'),
      dataIndex: 'isNotificationRecipient',
      key: 'isNotificationRecipient',
      width: 120,
      render: (isRecipient: boolean, record: RecipientUser) => (
        <Switch
          checked={isRecipient}
          loading={updatingRecipientIds.has(record.id)}
          onChange={(checked) => handleRecipientToggle(record.id, checked)}
        />
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  const isTelegramLinked = !!preferences?.telegramChatId;
  const isTelegramConfigured = serviceStatus?.telegram.configured;
  const isRecipient = preferences?.isNotificationRecipient ?? false;

  return (
    <div>
      <Title level={2}>
        <BellOutlined style={{ marginRight: 8 }} />
        {t('notifications:settings_page.title')}
      </Title>

      {/* Service Status - admin only */}
      {serviceStatus && canManageGlobalSettings && (
        <Card title={t('notifications:service_status.title')} style={{ marginTop: 24 }}>
          <Alert
            message={
              serviceStatus.email.configured
                ? t('notifications:service_status.emailConfigured')
                : t('notifications:service_status.emailNotConfigured')
            }
            type={serviceStatus.email.configured ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Alert
            message={
              serviceStatus.telegram.configured
                ? `${t('notifications:service_status.telegramConfigured')} (@${serviceStatus.telegram.botUsername})`
                : t('notifications:service_status.telegramNotConfigured')
            }
            type={serviceStatus.telegram.configured ? 'success' : 'info'}
            showIcon
          />
        </Card>
      )}

      {/* My Preferences Card - visible to all users */}
      <Card title={t('notifications:preferences_page.title')} style={{ maxWidth: 600, marginTop: 24 }}>
        {isRecipient ? (
        <Form
          form={prefsForm}
          layout="vertical"
          onFinish={handleSavePrefs}
          onFinishFailed={() => feedback.warning(t('notifications:fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="emailEnabled"
            label={t('notifications:preferences_page.emailNotifications')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 24 }}>
            {t('notifications:preferences_page.emailEnabled')}
          </Text>

          <Divider />

          <Form.Item
            name="telegramEnabled"
            label={t('notifications:preferences_page.telegramNotifications')}
            valuePropName="checked"
          >
            <Switch disabled={!isTelegramLinked || !isTelegramConfigured} />
          </Form.Item>

          {isTelegramConfigured ? (
            <Space direction="vertical" style={{ marginTop: -16, marginBottom: 24, width: '100%' }}>
              {isTelegramLinked ? (
                <>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {t('notifications:preferences_page.telegramLinked')} (@{preferences?.telegramUsername})
                  </Tag>
                  <Button size="small" danger onClick={handleUnlinkTelegram}>
                    {t('notifications:preferences_page.unlinkTelegram')}
                  </Button>
                </>
              ) : (
                <>
                  <Tag icon={<ExclamationCircleOutlined />} color="warning">
                    {t('notifications:preferences_page.telegramNotLinked')}
                  </Tag>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => setTelegramModalVisible(true)}
                  >
                    {t('notifications:preferences_page.linkTelegram')}
                  </Button>
                </>
              )}
            </Space>
          ) : (
            <Alert
              message={t('notifications:service_status.telegramNotConfigured')}
              type="info"
              showIcon
              style={{ marginTop: -16, marginBottom: 24 }}
            />
          )}

          <Divider />

          <Form.Item
            name="language"
            label={t('notifications:preferences_page.language')}
          >
            <Select
              options={[
                { value: 'pl', label: 'Polski' },
                { value: 'en', label: 'English' },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={savingPrefs}
              icon={<SaveOutlined />}
            >
              {t('notifications:preferences_page.save')}
            </Button>
          </Form.Item>
        </Form>
        ) : (
          <Alert
            message={t('notifications:preferences_page.notAuthorized')}
            type="warning"
            showIcon
          />
        )}
      </Card>

      {/* Admin-only sections */}
      {canManageAdminSections && (
        <>
          <Divider />

          <Title level={3}>{t('notifications:settings_page.eventType')}</Title>
          <Text type="secondary">{t('notifications:settings_page.description')}</Text>

          <Card style={{ marginTop: 24 }}>
            <Table
              dataSource={settings}
              columns={columns}
              rowKey="id"
              pagination={false}
            />
          </Card>

          <ConfirmationNoticeRecipientsCard />

          <Divider />

          <Title level={3}>
            <UserOutlined style={{ marginRight: 8 }} />
            {t('notifications:recipients.title')}
          </Title>
          <Text type="secondary">{t('notifications:recipients.description')}</Text>

          <Card style={{ marginTop: 24 }}>
            <Table
              dataSource={recipients}
              columns={recipientColumns}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </>
      )}

      <TelegramLinkModal
        visible={telegramModalVisible}
        onClose={() => setTelegramModalVisible(false)}
        onLinked={() => { setTelegramModalVisible(false); fetchData(); }}
      />
    </div>
  );
};

export default NotificationSettings;
